import { Input } from '@ohear/ui'
import { type FormEvent, useState } from 'react'
import { Panel, SlpButton } from '../components/slp'
import { API_URL, VERSION_OVERRIDE } from '../lib/api'
import { authClient } from '../lib/better-auth'

/**
 * Auth trio screen (F4) — replaces the inline sign-in form on /new.
 *
 *   - Sign in  : email + password + Google + forgot link
 *   - Sign up  : audited actor provisioning endpoint + magic link
 *   - Forgot   : Better Auth password-reset email → /reset-password
 *
 * i18n note: the session SPA is not wired to an i18n runtime yet, so copy is
 * inline English — user-facing, so actors see English only. The canonical
 * trilingual (en/es/zh) source lives in packages/shared/src/copy/auth-rt.ts —
 * swap these literals for it when the session app grows a locale switch.
 */

type Mode = 'signin' | 'signup' | 'forgot'

const GOOGLE_NOT_ENABLED =
  'Google sign-in is not enabled yet. Email + password works in the meantime.'

function GoogleMark() {
  return (
    <svg width="16" height="16" viewBox="0 0 18 18" aria-hidden="true">
      <path
        d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"
        fill="#4285F4"
      />
      <path
        d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"
        fill="#34A853"
      />
      <path
        d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"
        fill="#FBBC05"
      />
      <path
        d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"
        fill="#EA4335"
      />
    </svg>
  )
}

export function AuthScreen() {
  const [mode, setMode] = useState<Mode>('signin')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  /** Green-ish info notice (check-your-email states). */
  const [notice, setNotice] = useState<string | null>(null)

  // shared fields
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [displayName, setDisplayName] = useState('')

  const switchMode = (m: Mode) => {
    setMode(m)
    setError(null)
    setNotice(null)
  }

  const onSignIn = async (e: FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setError(null)
    const { error: err } = await authClient.signIn.email({ email, password })
    setBusy(false)
    if (err) setError(err.message ?? 'Sign-in failed.')
    // success: Better Auth's session hook swaps to the host view
  }

  const onSignUp = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    setNotice(null)
    setBusy(true)
    try {
      const response = await fetch(`${API_URL}/api/v1/auth/signup-actor`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'content-type': 'application/json',
          ...(VERSION_OVERRIDE
            ? {
                'Cloudflare-Workers-Version-Overrides': `ohear-api="${VERSION_OVERRIDE}"`,
              }
            : {}),
        },
        body: JSON.stringify({ email, name: displayName.trim() || email.split('@')[0] }),
      })
      const body = (await response.json().catch(() => ({}))) as {
        message?: string
        error?: { message?: string }
      }
      if (!response.ok) throw new Error(body.error?.message ?? `Signup failed (${response.status})`)
      setNotice(
        body.message ??
          'If that email is eligible, we sent a sign-in link. Check your inbox and spam.',
      )
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create the account.')
    } finally {
      setBusy(false)
    }
  }

  const onForgot = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    setNotice(null)
    setBusy(true)
    const { error: err } = await authClient.requestPasswordReset({
      email,
      redirectTo: `${window.location.origin}/reset-password`,
    })
    setBusy(false)
    if (err) {
      setError(err.message ?? 'Could not send the reset email.')
      return
    }
    setNotice(
      'If that address has an account, a password reset email is on its way — check your inbox and spam. The link brings you back here to set a new password.',
    )
  }

  /**
   * Google OAuth through Better Auth. The provider remains hidden behind the
   * same friendly error until Cloudflare receives the Google client secrets.
   */
  const onGoogle = async () => {
    setError(null)
    setNotice(null)
    setBusy(true)
    try {
      const { data, error: err } = await authClient.signIn.social({
        provider: 'google',
        callbackURL: `${window.location.origin}/new`,
        errorCallbackURL: `${window.location.origin}/new`,
        disableRedirect: true,
      })
      if (err) throw err
      if (!data?.url) throw new Error(GOOGLE_NOT_ENABLED)
      window.location.assign(data.url)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Google sign-in failed.')
    } finally {
      setBusy(false)
    }
  }

  const tabBtn = (m: Mode, label: string, testid: string) => (
    <button
      type="button"
      data-testid={testid}
      onClick={() => switchMode(m)}
      className={
        'flex-1 rounded-t-md px-3 py-2 text-[13px] font-semibold transition-colors ' +
        (mode === m
          ? 'bg-[var(--slp-blue)] text-[var(--slp-blue-text)]'
          : 'bg-transparent text-[var(--slp-text-dim)] hover:text-[var(--slp-text)]')
      }
    >
      {label}
    </button>
  )

  return (
    // G1 v2.8 #4 — desktop SaaS sign-in: 1180px canvas, left 60% product
    // visual (SLP blue), right 40% auth panel. No mobile-style centered card.
    <div className="min-h-screen min-w-[1180px] bg-[var(--slp-page)]">
      <div className="mx-auto flex min-h-screen w-[1180px] items-center gap-3 py-10">
        {/* ── left 60%: product visual ─────────────────────────────────── */}
        <div className="flex h-[560px] w-[700px] shrink-0 flex-col justify-between overflow-hidden rounded-md border border-[var(--slp-blue-active)] bg-gradient-to-br from-[#29ABE2] to-[#177bb0] p-8 text-white shadow-sm">
          <div>
            <h1 className="text-[34px] font-semibold tracking-tight">OHEAR Session</h1>
            <p className="mt-1 text-[14px] text-white/85">
              Live remote recording for ADR and dubbing — studio-grade, in the browser.
            </p>
          </div>
          {/* decorative SLP meter bars */}
          <div aria-hidden="true" className="flex items-end gap-1.5">
            {[34, 58, 22, 71, 46, 88, 39, 64, 27, 76, 52, 90, 31, 60, 44, 82, 25, 68, 50, 73].map(
              (h, i) => (
                <span
                  // eslint-disable-next-line react/no-array-index-key
                  key={i}
                  className="w-[14px] rounded-sm bg-white/30"
                  style={{ height: `${h}px` }}
                />
              ),
            )}
          </div>
          <ul className="flex flex-col gap-2 text-[13px] leading-snug text-white/90">
            <li>
              <b className="text-white">Lossless local BWF capture</b> — 48 kHz / 24-bit on the
              talent's machine, auto-uploaded with embedded timecode.
            </li>
            <li>
              <b className="text-white">SessionLink-style control room</b> — pro meters, timecode,
              channel setup, per-guest connection health.
            </li>
            <li>
              <b className="text-white">Program feed + video + chat</b> — picture, talkback and
              direction in the same window.
            </li>
          </ul>
        </div>

        {/* ── right 40%: auth panel ────────────────────────────────────── */}
        <div className="flex min-w-0 flex-1 flex-col">
          <p className="mb-3 text-[12px] text-[var(--slp-text-dim)]">
            Sign in to host a live remote recording session.
          </p>

          {mode !== 'forgot' ? (
        <div className="flex gap-1 px-1" data-testid="rt-auth-tabs">
          {tabBtn('signin', 'Sign in', 'rt-auth-tab-signin')}
          {tabBtn('signup', 'Sign up', 'rt-auth-tab-signup')}
        </div>
      ) : null}

      <Panel
        title={mode === 'signin' ? 'Sign in' : mode === 'signup' ? 'Create account' : 'Reset password'}
      >
        {mode === 'signin' ? (
          <form onSubmit={(e) => void onSignIn(e)} className="flex flex-col gap-3">
            <Input
              type="email"
              placeholder="Email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="rt-login-email"
            />
            <Input
              type="password"
              placeholder="Password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="rt-login-password"
            />
            {error ? <p className="text-[13px] text-[var(--amber)]">{error}</p> : null}
            {notice ? (
              <p className="text-[13px] text-[var(--slp-text)]" data-testid="rt-auth-notice">
                {notice}
              </p>
            ) : null}
            <SlpButton type="submit" disabled={busy} data-testid="rt-auth-signin">
              {busy ? 'Signing in…' : 'Sign in'}
            </SlpButton>
            <SlpButton
              variant="secondary"
              onClick={() => void onGoogle()}
              disabled={busy}
              data-testid="rt-auth-google"
              className="gap-2"
            >
              <GoogleMark />
              Continue with Google
            </SlpButton>
            <button
              type="button"
              onClick={() => switchMode('forgot')}
              data-testid="rt-auth-forgot"
              className="self-start text-[13px] text-[var(--slp-blue)] hover:underline"
            >
              Forgot password?
            </button>
          </form>
        ) : null}

        {mode === 'signup' ? (
          <form onSubmit={(e) => void onSignUp(e)} className="flex flex-col gap-3">
            <Input
              placeholder="Display name"
              autoComplete="name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              data-testid="rt-signup-name"
            />
            <Input
              type="email"
              placeholder="Email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="rt-signup-email"
            />
            {error ? <p className="text-[13px] text-[var(--amber)]">{error}</p> : null}
            {notice ? (
              <p className="text-[13px] text-[var(--slp-text)]" data-testid="rt-auth-notice">
                {notice}
              </p>
            ) : null}
            <SlpButton type="submit" disabled={busy} data-testid="rt-auth-signup">
              {busy ? 'Creating account…' : 'Create account'}
            </SlpButton>
            <SlpButton
              variant="secondary"
              onClick={() => void onGoogle()}
              disabled={busy}
              data-testid="rt-auth-google-signup"
              className="gap-2"
            >
              <GoogleMark />
              Continue with Google
            </SlpButton>
          </form>
        ) : null}

        {mode === 'forgot' ? (
          <form onSubmit={(e) => void onForgot(e)} className="flex flex-col gap-3">
            <p className="text-[13px] text-[var(--slp-text-dim)]">
              Enter your account email and we will send a link to set a new password.
            </p>
            <Input
              type="email"
              placeholder="Email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="rt-forgot-email"
            />
            {error ? <p className="text-[13px] text-[var(--amber)]">{error}</p> : null}
            {notice ? (
              <p className="text-[13px] text-[var(--slp-text)]" data-testid="rt-auth-notice">
                {notice}
              </p>
            ) : null}
            <SlpButton type="submit" disabled={busy || !email} data-testid="rt-auth-forgot-submit">
              {busy ? 'Sending…' : 'Send reset link'}
            </SlpButton>
            <button
              type="button"
              onClick={() => switchMode('signin')}
              className="self-start text-[13px] text-[var(--slp-blue)] hover:underline"
            >
              Back to sign in
            </button>
          </form>
        ) : null}
          </Panel>
        </div>
      </div>
    </div>
  )
}
